import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useFundContext from '@/contexts/Fund/useFundContext';
import { useGetApi, useGetAuthApi } from '@/hooks/UseApiCalls';
import { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import { LandingSitePublishedFormsDtoOut } from '@/models/out/LandingSitePublishedFormsDtoOut';
import { RegulationsAcceptanceStatusDtoIn } from '@/models/in/RegulationsAcceptanceStatusDtoIn';
import { downloadStoredDocument } from '@/helpers/downloadFile';
import { formatDateLocale } from '@/helpers/dateHelper';
import eyeIcon from '@/assets/icons/eye.svg';
import { REGULATIONS_PAGE_PATH } from '@/pages/Regulations/RegulationsPage';
import DocumentRow from './sections/DocumentRow';
import DonorKitSection from './sections/DonorKitSection';
import ApprovalsSection from './sections/ApprovalsSection';
import OpenFundSection from './sections/OpenFundSection';
import styles from './FundSettings.module.css';

/**
 * «אישורים ומסמכים» (HAT-1231) - what the coordinator hands on, arranged by who receives it:
 * the donor, the accountant, and the fund's own file. Read-only here; the office is where any of
 * it is changed. Its own item in the menu, behind its own flag, because an approval and a
 * standing-order form do not define anything about the fund.
 *
 * Druk, 4.10: the overheads panel is not shown for now (OverheadsSection stays, unused, for when
 * it returns), nor the links for referring someone to open a welfare or community fund; and the
 * opening agreement row appears only when the fund has one on record.
 *
 * HAT-1370: the coordinator's own way to open another project closes the page.
 */
const FundDocumentsPage = () => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const [isDownloadingAgreement, setIsDownloadingAgreement] = useState(false);
  const [isDownloadingRules, setIsDownloadingRules] = useState(false);

  // The «openable» reading of the catalogue, not the one the site itself reads: an entry that is
  // still the site's own copy is a path on the site («/docs/tofes-46.pdf»), which would resolve
  // against the portal's own host and lead nowhere. Loaded once, for every panel that reads it.
  const { data: forms, isError: isFormsError } = useGetApi<LandingSitePublishedFormsDtoOut>('landing-site-forms/openable');

  const { data: agreement, isLoading: isAgreementLoading, isError: isAgreementError } = useGetAuthApi<DocumentDtoOut>(
    currentFund ? `funds/financial-data/${currentFund.id}/documents/agreement` : ''
  );
  const { data: rules } = useGetAuthApi<RegulationsAcceptanceStatusDtoIn>('regulations-acceptance/status');

  if (!currentFund) return <h1>{t('noFundSelected')}.</h1>;

  // A document DTO carries its link only once the file is really on record, so the row offers its
  // actions only when there is something behind them.
  const agreementFile = !isAgreementError && agreement?.uri ? { uri: agreement.uri, name: agreement.name } : null;
  const rulesFile = rules?.acceptanceDocument?.uri ? rules.acceptanceDocument : null;

  // Stored documents sit in our storage behind a link signed for viewing, so saving one goes
  // through the API like every other stored document (what DownloadFileButton does, with a label).
  const downloadStored = async (uri: string, fileName: string, setBusy: (busy: boolean) => void) => {
    setBusy(true);
    try {
      await downloadStoredDocument(uri, fileName || t('downloadFile.fallbackFileName'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>{t('fundSettingsDocumentsPage.title')}</h1>

      {isFormsError && <p className={styles.noticeError}>{t('fundSettingsDocumentsPage.catalogUnavailable')}</p>}

      <DonorKitSection fundId={currentFund.id} forms={forms} />

      <ApprovalsSection forms={forms} />

      <div className={styles.panel}>
        <h2 className={styles.panelTitle}>{t('fundSettingsDocumentsPage.fundDocumentsTitle')}</h2>
        <div className={styles.documentList}>
          {/* Only when the fund has one on record: a row that only says there is nothing is noise. */}
          {!isAgreementLoading && agreementFile && (
            <DocumentRow
              name={t('fundSettingsDocumentsPage.agreementTitle')}
              note={t('fundSettingsDocumentsPage.agreementNote')}
              href={agreementFile.uri}
              onDownload={() => downloadStored(agreementFile.uri, agreementFile.name, setIsDownloadingAgreement)}
              isBusy={isDownloadingAgreement}
            />
          )}
          {/* The one document in the system the coordinator signed - listed with the fund's own. */}
          <DocumentRow
            name={t('fundSettingsDocumentsPage.regulationsTitle')}
            note={rulesFile && rules?.acceptedAt ? t('fundSettingsDocumentsPage.regulationsAcceptedAt', { date: formatDateLocale(rules.acceptedAt) }) : undefined}
            href={rulesFile?.uri}
            onDownload={rulesFile ? () => downloadStored(rulesFile.uri!, rulesFile.name, setIsDownloadingRules) : undefined}
            isBusy={isDownloadingRules}
          >
            {/* In this tab, not a new one: the regulations page has its own «חזרה» now (Druk, 4.10),
                and a new tab whose way out opened the site again left the site open twice
                (Chaya, 4.10). */}
            {!rulesFile && (
              <Link to={REGULATIONS_PAGE_PATH} className={styles.linkBtn}>
                <img src={eyeIcon} alt="" className={styles.actionIcon} />
                {t('fundSettingsDocumentsPage.regulationsPublic')}
              </Link>
            )}
          </DocumentRow>
        </div>
      </div>

      <OpenFundSection />
    </div>
  );
};

export default FundDocumentsPage;
